import React, {useEffect, useState} from 'react';
import './UserProfile.scss';
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import { Box, Avatar, IconButton } from "@mui/material";
import { PhotoCamera } from "@mui/icons-material";
import { getCurrentUser, updateUserAttributes, fetchUserAttributes } from 'aws-amplify/auth';
import { uploadData, getUrl } from 'aws-amplify/storage';

function UserProfile (props) {

	const [email, setEmail] = useState('');
	const [message, setMessage] = useState(null);
	const [error, setError] = useState(null);
	const [avatarUrl, setAvatarUrl] = useState(null);
	const [username, setUsername] = useState('');

	async function changeEmail(e) {
		e.preventDefault(); // Use preventDefault instead of stopPropagation
		
		// Reset messages
		setMessage(null);
		setError(null);
		
		// Basic email validation
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(email)) {
			setError('Please enter a valid email address');
			return;
		}
		
		try {
			// Then update attributes
			await updateUserAttributes({
				userAttributes: {
					email: email
				}
			});
			setMessage('Email updated successfully. Please check your inbox for verification.');
		} catch (error) {
			console.log('error', error);
			setError(error.message || 'Failed to update email');
		}
	}

	// Function to resize and crop image
	const resizeImage = (file) => {
		return new Promise((resolve) => {
			const canvas = document.createElement('canvas');
			const ctx = canvas.getContext('2d');
			const img = new Image();
			
			img.onload = () => {
				const { width, height } = img;
				const size = 160;
				
				// Calculate scale to fit shortest side to 160px
				const scale = size / Math.min(width, height);
				const scaledWidth = width * scale;
				const scaledHeight = height * scale;
				
				// Calculate crop offsets to center the image
				const offsetX = (scaledWidth - size) / 2;
				const offsetY = (scaledHeight - size) / 2;
				
				canvas.width = size;
				canvas.height = size;
				
				// Draw and crop the image
				ctx.drawImage(img, -offsetX, -offsetY, scaledWidth, scaledHeight);
				
				canvas.toBlob(resolve, 'image/jpeg', 0.9);
			};
			
			img.src = URL.createObjectURL(file);
		});
	};

	// Function to upload avatar
	const uploadAvatar = async (file) => {
		try {
			const resizedFile = await resizeImage(file);
			const key = `avatars/${username}.jpg`;
			
			await uploadData({
				key,
				data: resizedFile,
				options: {
					accessLevel: 'public'
				}
			});
			
			// Update user attributes with avatar key
			await updateUserAttributes({
				userAttributes: {
					picture: key
				}
			});
			
			// Refresh avatar URL
			const url = await getUrl({ key, options: { accessLevel: 'public' } });
			setAvatarUrl(url.url);
			setMessage('Avatar updated successfully!');
		} catch (error) {
			console.error('Error uploading avatar:', error);
			setError('Failed to upload avatar');
		}
	};

	// Handle file selection
	const handleAvatarChange = (event) => {
		const file = event.target.files[0];
		if (file && file.type.startsWith('image/')) {
			uploadAvatar(file);
		} else {
			setError('Please select a valid image file');
		}
	};

	useEffect(() => {
		async function fetchUserData() {
			try {
				const { username: currentUsername } = await getCurrentUser();
				setUsername(currentUsername);
				
				const attributes = await fetchUserAttributes();
				setEmail(attributes.email);
				
				// Load avatar if exists
				if (attributes.picture) {
					try {
						const url = await getUrl({ 
							key: attributes.picture, 
							options: { accessLevel: 'public' } 
						});
						setAvatarUrl(url.url);
					} catch (error) {
						console.log('Avatar not found, using default');
					}
				}
			} catch (error) {
				console.log('Error fetching user data:', error);
			}
		}
		fetchUserData();
	}, [])

	return (
		<div>
			{message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}
			{error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
			
			{/* Avatar Section */}
			<Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
				<Avatar 
					src={avatarUrl} 
					alt={username}
					sx={{ width: 160, height: 160, mr: 2 }}
				>
					{username?.charAt(0)?.toUpperCase()}
				</Avatar>
				<input
					accept="image/*"
					style={{ display: 'none' }}
					id="avatar-upload"
					type="file"
					onChange={handleAvatarChange}
				/>
				<label htmlFor="avatar-upload">
					<IconButton color="primary" component="span">
						<PhotoCamera />
					</IconButton>
				</label>
			</Box>
			
			{/* Email Form */}
			<form onSubmit={changeEmail}>
				<div>
					<TextField
						id="outlined-email-input"
						label="Email"
						type="email"
						value={email}
						onChange={(event) => setEmail(event.target.value)}
						fullWidth
						margin="normal"
						required
					/>
				</div>
				<Button type="submit" variant="contained">Change Email</Button>
			</form>
		</div>
	);

}

export default UserProfile;
